import { useEffect, useRef, useState } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import { Save, Download, Printer, FilePlus, ChevronDown, Lock, Cloud, LogIn } from 'lucide-react';
import { useNavGuard } from '@/contexts/NavGuardContext';

/** One Save action for the active destination; copy exports are explicit. */
export default function SaveMenu({
  // New API
  docSource = 'new',          // 'cloud' | 'device' | 'new'
  cloudState = 'signed-out',   // 'available' | 'locked' | 'signed-out'
  platform = 'web',            // 'desktop' | 'web'
  cloudUpsell = null,          // { price, ctaShort, … } — from getCloudUpsell
  // Handlers
  onSave,
  onSaveAs,
  onDownload,
  onSaveToCloud,
  onPrint,
  onFlatten,
  // Visual state
  hasOverlays,
  hasUnsavedChanges,
  disabled,
  // Back-compat shim — old callers may still pass `mode`
  mode,
}) {
  const navigate = useNavigate();
  const guard = useNavGuard();
  const { t } = useTranslation();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  // Back-compat: translate old `mode` prop into `platform` if caller hasn't
  // migrated yet. Remove once all callers pass the new props.
  const effectivePlatform = mode === 'desktop' ? 'desktop' : mode === 'web' ? 'web' : platform;
  const isDesktop = effectivePlatform === 'desktop';

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [open]);

  const saveLabel = t('common:buttons.save', 'Save');

  // Primary button: what ⌘S should do right now.
  const primary = (() => {
    if (docSource === 'cloud') {
      return { label: saveLabel, icon: Save, onClick: onSave };
    }
    if (isDesktop) {
      return { label: saveLabel, icon: Save, onClick: onSave };
    }
    return { label: t('common:buttons.download', 'Download'), icon: Download, onClick: onSave };
  })();

  const PrimaryIcon = primary.icon;

  const goToUpgrade = () => {
    setOpen(false);
    // Route through nav guard so the Viewer's UnsavedWarningDialog fires
    // if the user has pending edits.
    guard(() => navigate('/account/billing?highlight=cloud'));
  };
  const goToSignIn = () => {
    setOpen(false);
    guard(() => navigate('/login?next=/cloud'));
  };

  // Exports keep the active destination. Save As and Move to cloud change it.
  const items = [];

  items.push({
    id: 'device', label: isDesktop ? 'Export a copy…' : 'Download a copy',
    icon: Download, onClick: onDownload, disabled: !onDownload,
  });

  // 2. Cloud row — renders one of three states
  if (cloudState === 'available') {
    items.push({
      id: 'cloud',
      label: docSource === 'cloud' ? 'Save to cloud' : 'Move to cloud',
      icon: Cloud,
      onClick: onSaveToCloud,
      shortcut: docSource === 'cloud' ? '⌘S' : null,
      emphasized: docSource === 'cloud' || docSource === 'device',
      disabled: !onSaveToCloud,
    });
  } else if (cloudState === 'locked') {
    const cta = cloudUpsell?.ctaShort || 'Unlock cloud';
    items.push({
      id: 'cloud-locked',
      label: docSource === 'cloud' ? 'Save to cloud' : 'Move to cloud',
      icon: Cloud,
      onClick: goToUpgrade,
      lockBadge: <Lock size={11} className="text-gray-400" />,
      subLabel: <span className="text-[10px] font-semibold text-brand-blue">{cta} →</span>,
      locked: true,
    });
  } else {
    items.push({
      id: 'cloud-signedout',
      label: docSource === 'cloud' ? 'Save to cloud' : 'Move to cloud',
      icon: Cloud,
      onClick: goToSignIn,
      lockBadge: <LogIn size={11} className="text-gray-400" />,
      subLabel: <span className="text-[10px] font-semibold text-brand-blue">{t('common:cloud.sign_in_to_continue', 'Sign in to continue →')}</span>,
      locked: true,
    });
  }

  // 3. Save As… (desktop only — web has no real analog beyond Download)
  if (isDesktop && onSaveAs) {
    items.push({
      id: 'saveas',
      label: t('common:buttons.save_as', 'Save As…'),
      icon: FilePlus,
      onClick: onSaveAs,
      shortcut: '⌘⇧S',
    });
  }

  // 4. Divider + Print
  items.push({ divider: true, id: 'div-print' });
  items.push({ id: 'print', label: t('common:buttons.print', 'Print'), icon: Printer, onClick: onPrint });

  // Flatten only the exported copy.
  if (hasOverlays && onFlatten) {
    items.push({
      id: 'flatten',
      label: 'Export flattened copy…',
      icon: Lock,
      onClick: onFlatten,
      destructive: true,
    });
  }

  return (
    <div className="relative inline-flex items-stretch" ref={ref}>
      <button
        onClick={() => { if (!disabled) primary.onClick?.(); }}
        disabled={disabled}
        className="relative flex items-center gap-1.5 px-3 py-2 sm:py-1 bg-brand-blue text-white text-sm rounded-l-md font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
        title={primary.label}
      >
        <PrimaryIcon size={13} /> {primary.label}
        {hasUnsavedChanges && (
          <span className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-white pointer-events-none" />
        )}
      </button>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="px-2 sm:px-1.5 py-2 sm:py-1 bg-brand-blue text-white rounded-r-md border-l border-white/20 hover:bg-brand-dark transition-colors disabled:opacity-50 min-w-[44px] sm:min-w-0 min-h-[44px] sm:min-h-0 flex items-center justify-center"
        title={`${primary.label} options`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <ChevronDown size={13} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[220px] max-w-[calc(100vw-16px)]"
        >
          {items.map((item) => {
            if (item.divider) return <div key={item.id} className="h-px bg-gray-100 my-1" />;
            const ItemIcon = item.icon;
            const rowBase = 'w-full flex items-start gap-2 px-3 py-1.5 text-sm transition-colors text-left';
            let rowColor = 'text-gray-700 hover:bg-gray-50';
            if (item.destructive) rowColor = 'text-amber-700 hover:bg-amber-50';
            else if (item.locked) rowColor = 'text-gray-500 hover:bg-brand-light/60';
            else if (item.emphasized) rowColor = 'text-brand-blue font-medium hover:bg-brand-light/60';

            return (
              <button
                key={item.id}
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  item.onClick?.();
                }}
                disabled={item.disabled}
                className={`${rowBase} ${rowColor} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <ItemIcon
                  size={14}
                  className={
                    item.destructive ? 'text-amber-600 mt-0.5' :
                    item.locked ? 'text-gray-400 mt-0.5' :
                    item.emphasized ? 'text-brand-blue mt-0.5' :
                    'text-gray-500 mt-0.5'
                  }
                />
                <span className="flex-1">
                  <span className="flex items-center gap-1.5">
                    {item.label}
                    {item.lockBadge}
                  </span>
                  {item.subLabel && <span className="block">{item.subLabel}</span>}
                </span>
                {item.shortcut && (
                  <span className="text-[10px] text-gray-400 font-mono tabular-nums mt-0.5">{item.shortcut}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
